import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Smartphone, Monitor, ExternalLink } from 'lucide-react';
import GooglePlayBadge from './components/GooglePlayBadge';
import { apps } from './data/apps';

const AppDetailPage = () => {
  const { slug } = useParams();
  const app = apps.find(a => a.slug === slug);

  if (!app) {
    return <Navigate to="/apps" replace />;
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "name": app.name,
    "description": app.fullDescription || app.description,
    "applicationCategory": app.category,
    "operatingSystem": "Android",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "author": {
      "@type": "Organization",
      "name": "Kabett",
      "url": "https://kabett.com"
    },
    "installUrl": app.playStoreUrl
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://kabett.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Apps",
        "item": "https://kabett.com/apps"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": app.name,
        "item": `https://kabett.com/apps/${app.slug}`
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{app.name} - {app.tagline} | Kabett Apps</title>
        <meta name="description" content={app.fullDescription || app.description} />
        <meta name="keywords" content={app.keywords.join(', ')} />
        <link rel="canonical" href={`https://kabett.com/apps/${app.slug}`} />
        <meta property="og:title" content={`${app.name} - ${app.tagline}`} />
        <meta property="og:description" content={app.description} />
        <meta property="og:url" content={`https://kabett.com/apps/${app.slug}`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbData)}
        </script>
      </Helmet>

      {/* Back Link */}
      <Link
        to="/apps"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8"
      >
        <ArrowLeft size={20} />
        <span>All Apps</span>
      </Link>

      {/* Hero Section */}
      <section className="flex flex-col md:flex-row gap-8 mb-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex-shrink-0"
        >
          <img
            src={app.icon}
            alt={`${app.name} App Icon`}
            className="w-32 h-32 md:w-40 md:h-40 rounded-3xl shadow-xl mx-auto md:mx-0"
          />
        </motion.div>
        <div className="flex-grow">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl md:text-4xl font-serif font-bold mb-2">{app.name}</h1>
            <p className="text-lg text-gray-500 mb-4">{app.tagline}</p>

            {/* Platform badges */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                <Smartphone size={16} />
                <span>Android</span>
              </div>
              {app.hasChromeExtension && (
                <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                  <Monitor size={16} />
                  <span>Chrome Extension</span>
                </div>
              )}
            </div>

            <p className="text-gray-600 text-lg mb-6">
              {app.fullDescription || app.description}
            </p>

            {/* Download buttons */}
            <div className="flex flex-wrap items-center gap-4">
              <GooglePlayBadge url={app.playStoreUrl} />
              {app.hasChromeExtension && (
                <a
                  href={app.chromeExtensionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gray-800 text-white px-5 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Monitor size={20} />
                  <span>Chrome Web Store</span>
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      {app.features && app.features.length > 0 && (
        <section className={`${app.bgColor} rounded-lg p-8 mb-12`}>
          <h2 className="text-2xl font-serif font-bold mb-6">Features</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {app.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#BAB37A] flex items-center justify-center">
                  <Check size={14} className="text-white" />
                </div>
                <span className="text-gray-700">{feature}</span>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="text-center py-8">
        <h2 className="text-2xl font-serif font-bold mb-4">Ready to try {app.name}?</h2>
        <p className="text-gray-600 mb-6">Download now for free on Google Play</p>
        <GooglePlayBadge url={app.playStoreUrl} />
      </section>
    </>
  );
};

export default AppDetailPage;
